import { useState, useEffect } from "react";

// ⚠️ URL de la mascotte Renart — à adapter si tu héberges le fichier ailleurs.
// (fichier renart.webp fourni séparément, à uploader une seule fois sur le serveur)
const FOX_IMG_URL = "https://foxaya.fr/blog/assets/renard.webp";

const MENU_ITEMS = [
  { tag: "Rentabilité",  label: "Améliorez votre rentabilité" },
  { tag: "Clients",      label: "Trouvez et fidélisez vos clients" },
  { tag: "RH",           label: "Attirez des talents" },
  { tag: "Conformité",   label: "Vous mettre en conformité" },
  { tag: "Réputation",   label: "Améliorez votre image" },
  { tag: "Avenir",       label: "Sécurisez et pérennisez votre activité" },
];

// Valeurs de secours si le fichier partagé ne peut pas être chargé.
const DEFAULT_PEPITE = {
  date: "2026-05-13",
  titre: "Titre",
  texte: "Texte",
  source: "Source",
  lien: {
    url: "/blog/sensibilisation/",
    titre: "Climat et environnement : où en est-on ?",
    texte: "Pas de catastrophisme : des faits, des chiffres et des opportunités pour les entrepreneurs qui veulent agir.",
  },
};

const sharedPepite = () =>
  typeof window !== "undefined" && window.FOXAYA_PEPITE
    ? (window.FOXAYA_PEPITE.config || window.FOXAYA_PEPITE)
    : DEFAULT_PEPITE;

const btn = {
  width: 44, height: 44, borderRadius: 10,
  background: "rgba(30,50,65,0.95)",
  border: "1px solid rgba(255,255,255,0.2)",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer",
};

function hasSeenToday(pepite = sharedPepite()) {
  try {
    const seen = localStorage.getItem('planetSeen');
    if (!seen) return false;
    return seen === pepite.date;
  } catch { return false; }
}

function markSeenToday(pepite = sharedPepite()) {
  try { localStorage.setItem('planetSeen', pepite.date); } catch {}
}

// Hauteur de la bannière prototype, réutilisée pour décaler la navbar
// vers le bas quand elle est affichée (et remise à 0 une fois fermée).
const PROTO_BANNER_HEIGHT = 40;

function isBannerDismissed() {
  try { return localStorage.getItem('protoBannerDismissed') === '1'; } catch { return false; }
}

export default function NavbarPortal() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [planetOpen, setPlanetOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('hero');
  const [scrolled, setScrolled] = useState(false);
  const [pepite, setPepite] = useState(sharedPepite);
  const [hasNotif, setHasNotif] = useState(() => !hasSeenToday(sharedPepite()));
  const [showBanner, setShowBanner] = useState(() => !isBannerDismissed());
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const isMenu = currentPage !== "hero";

  useEffect(() => {
    const handler = (e) => setCurrentPage(e.detail.page);
    window.addEventListener('pageChange', handler);
    return () => window.removeEventListener('pageChange', handler);
  }, []);

  // Permet à n'importe quel composant (par exemple le renard de HeroPage)
  // d'ouvrir cette même pépite, sans dupliquer son contenu.
  useEffect(() => {
    const openSharedPepite = () => openPlanet();
    window.addEventListener('foxaya:open-pepite', openSharedPepite);
    return () => window.removeEventListener('foxaya:open-pepite', openSharedPepite);
  }, [pepite, hasNotif]);

  // Charge une seule configuration commune à toutes les pages React et HTML.
  useEffect(() => {
    const syncPepite = () => {
      const next = sharedPepite();
      setPepite(next);
      setHasNotif(!hasSeenToday(next));
    };
    window.addEventListener('foxaya:pepite-ready', syncPepite);

    if (typeof window.FOXAYA_PEPITE?.open === 'function') {
      syncPepite();
    } else if (!document.querySelector('script[src*="pepite-verte.js"]')) {
      const script = document.createElement('script');
      // Le paramètre de version évite qu'un ancien pepite-config.js conservé
      // dans le cache empêche le nouveau composant de se charger.
      script.src ='/blog/assets/pepite-verte.js?v=20260820-1';
      script.async = true;
      script.dataset.foxayaPepite = 'true';
      script.addEventListener('load', syncPepite, { once: true });
      document.head.appendChild(script);
    }

    return () => window.removeEventListener('foxaya:pepite-ready', syncPepite);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('foxaya_user');
      if (stored) setUser(JSON.parse(stored));
    } catch {}
    const onStorage = () => {
      try {
        const stored = localStorage.getItem('foxaya_user');
        setUser(stored ? JSON.parse(stored) : null);
      } catch {}
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('foxaya_login', onStorage);
    return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('foxaya_login', onStorage); };
  }, []);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) return;
    try {
      const res = await fetch('https://foxaya.fr/blog/api/connexion.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (data.success) {
        const userData = { ...data.user, lastLogin: Date.now() };
        localStorage.setItem('foxaya_user', JSON.stringify(userData));
        setUser(userData);
        setShowLogin(false);
        setLoginEmail(''); setLoginPassword(''); setLoginError('');
        window.location.href = '/blog/tableau-de-bord/';
      } else {
        setLoginError(data.error || 'Email ou mot de passe incorrect');
      }
    } catch { setLoginError('Erreur de connexion au serveur'); }
  };

  const handleDashboardClick = () => {
    if (user) {
      window.location.href = '/blog/tableau-de-bord/';
    } else {
      setShowLogin(true);
    }
  };

  const openPlanet = () => {
    if (window.FOXAYA_PEPITE?.open) {
      window.FOXAYA_PEPITE.open();
    } else {
      // Si l'utilisateur clique pendant le chargement, on ouvre la pépite
      // dès que le module commun est prêt.
      window.addEventListener('foxaya:pepite-ready', () => {
        window.FOXAYA_PEPITE?.open?.();
      }, { once: true });
    }
    if (hasNotif) {
      markSeenToday(pepite);
      setHasNotif(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes nbSlide {
          from { opacity:0; transform:translateY(-8px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes notifPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.3); }
        }
        @keyframes fadeIn {
          from { opacity:0; }
          to   { opacity:1; }
        }
        @keyframes slideUp {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .proto-banner-short { display: none; }
        @media (max-width: 600px) {
          .proto-banner-full { display: none; }
          .proto-banner-short { display: inline; }
        }
      `}</style>

      {/* ── BANNIÈRE PROTOTYPE ──
          Rappelle sur toutes les pages (même message que la popup d'arrivée)
          que le site est une démonstration. Fermable, mémorisée via
          localStorage pour ne pas re-harceler une fois fermée. */}
      {showBanner && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0,
          zIndex: 1000000, height: PROTO_BANNER_HEIGHT,
          background: "linear-gradient(90deg,#00A5B2,#9CD14D)",
          color: "#001c2b",
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 10, padding: "0 34px",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: 12.5, fontWeight: 600, textAlign: "center",
        }}>
          <span className="proto-banner-full">Vous consultez une version de démonstration, encore en cours de développement. Certains contenus ou fonctionnalités peuvent être erronés, incomplets, provisoires ou instables.</span>
          <span className="proto-banner-short">Version de démonstration : contenus ou fonctionnalités erronés, incomplets, provisoires ou instables.</span>
          <button
            onClick={() => { setShowBanner(false); try { localStorage.setItem('protoBannerDismissed', '1'); } catch {} }}
            aria-label="Fermer"
            style={{
              position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
              background: "rgba(0,28,43,0.15)", border: "none", borderRadius: "50%",
              width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center",
              color: "#001c2b", fontSize: 12, lineHeight: 1, cursor: "pointer",
            }}
          >✕</button>
        </div>
      )}

      {/* ── NAVBAR ── */}
      <div style={{
        position: "fixed", top: showBanner ? PROTO_BANNER_HEIGHT : 0, left: 0, right: 0,
        zIndex: 999999,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 30px",
        fontFamily: "'Montserrat', sans-serif",
        transition: "background 0.3s, box-shadow 0.3s, top 0.25s ease",
        background: scrolled ? "rgba(0,28,43,0.92)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(12px)" : "none",
        boxShadow: scrolled ? "0 1px 0 rgba(255,255,255,0.08)" : "none",
      }}>
        {/* Gauche */}
        <div style={{ display: "flex", gap: 8 }}>
          {isMenu && (
            <a href="#" onClick={(e) => { e.preventDefault(); window.history.back(); }} style={btn}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M11 4L6 9l5 5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          )}
          {isMenu && (
            <a href="/blog/" style={btn}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2 8.5L9 2l7 6.5V16a1 1 0 0 1-1 1H12v-4H6v4H3a1 1 0 0 1-1-1V8.5z" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          )}
        </div>

        {/* Droite — tableau de bord + planète + burger */}
        <div style={{ display: "flex", gap: 8 }}>

          {/* Tableau de bord / Connexion */}
          <div onClick={handleDashboardClick} style={{ ...btn, position: "relative", cursor: "pointer" }} title={user ? `Mon tableau de bord — ${user.prenom}` : 'Se connecter'}>
            {user ? (
              <>
                {/* Avatar initiale */}
                <div style={{
                  width: 26, height: 26, borderRadius: "50%",
                  background: "linear-gradient(135deg,#00A5B2,#9CD14D)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 800, color: "#001c2b",
                }}>
                  {user.prenom?.charAt(0).toUpperCase()}
                </div>
                {/* Point vert connecté */}
                <div style={{
                  position: "absolute", bottom: -2, right: -2,
                  width: 10, height: 10, borderRadius: "50%",
                  background: "#9CD14D", border: "2px solid rgba(0,28,43,0.9)",
                }} />
              </>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            )}
          </div>

          {/* Renart — pépite verte (même cadrage que .fox-mini/.fox-face sur index.html) */}
          <div style={{ position: "relative" }}>
            <div data-foxaya-pepite-trigger onClick={openPlanet} style={{ ...btn, cursor: "pointer" }} title="Renart, votre guide">
              <div style={{
                width: 26, height: 30, borderRadius: 6, overflow: "hidden", position: "relative",
                filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.3))",
              }}>
                <div style={{
                  position: "absolute", width: "220%", height: "220%", left: "-60%", top: "-30%",
                  backgroundImage: `url(${FOX_IMG_URL})`,
                  backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center 25%",
                }} />
              </div>
            </div>
            {hasNotif && (
              <div style={{
                position: "absolute", bottom: -2, right: -2, zIndex: 20,
                width: 12, height: 12,
                background: "#ef4444", borderRadius: "50%",
                border: "2px solid rgba(0,28,43,0.9)",
                animation: "notifPulse 1.5s ease-in-out infinite",
              }} />
            )}
          </div>

          {/* Burger */}
          <div onClick={() => setMenuOpen(o => !o)} style={{ ...btn, cursor: "pointer" }}>
            {menuOpen ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 4l10 10M14 4L4 14" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3 5h12M3 9h12M3 13h12" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* ── MENU BURGER ── */}
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{
            position: "fixed", inset: 0, zIndex: 999997,
            background: "rgba(0,0,0,0.5)",
          }} />
          <div style={{
            position: "fixed", top: 72, right: 16, zIndex: 999998,
            background: "#001c2b",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 16, padding: 6, minWidth: 260,
            boxShadow: "0 24px 64px rgba(0,0,0,0.7)",
            animation: "nbSlide 0.25s ease both",
            fontFamily: "'Montserrat', sans-serif",
          }}>
            {MENU_ITEMS.map((item, i) => (
              <div key={i}
                onClick={() => { window.dispatchEvent(new CustomEvent('pageChange', { detail: { page: 'menu', menuIndex: i } })); setMenuOpen(false); }}
                style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 12px", borderRadius:10, cursor:"pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(0,165,178,0.12)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{ width:30, height:30, borderRadius:8, flexShrink:0, background:"rgba(0,165,178,0.12)", border:"1px solid rgba(0,165,178,0.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:"#00A5B2" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:"#fff", lineHeight:1.3 }}>{item.tag}</div>
                  <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.4)", marginTop:2 }}>{item.label}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── POPUP CONNEXION ── */}
      {showLogin && (
        <>
          <div onClick={() => { setShowLogin(false); setLoginError(''); }} style={{
            position: "fixed", inset: 0, zIndex: 9999990,
            background: "rgba(0,0,0,0.7)",
          }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            zIndex: 9999991,
            width: "90vw", maxWidth: 400,
            background: "#fff", borderRadius: 20,
            padding: "2rem",
            fontFamily: "'Montserrat', sans-serif",
            boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
          }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#eaf6f6", border: "1.5px solid rgba(0,165,178,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#00A5B2" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#001c2b", marginBottom: 4 }}>Bon retour !</div>
              <div style={{ fontSize: 13, color: "#888" }}>Connectez-vous pour accéder à votre espace</div>
            </div>

            {/* Email */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#001c2b", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>Email</label>
              <input type="email" placeholder="votre@email.com" value={loginEmail}
                onChange={e => { setLoginEmail(e.target.value); setLoginError(''); }}
                style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${loginError ? '#ef4444' : '#e8e8e6'}`, borderRadius: 10, fontSize: 14, fontFamily: "'Montserrat', sans-serif", outline: "none", boxSizing: "border-box", color: "#001c2b" }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#001c2b", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>Mot de passe</label>
              <input type="password" placeholder="••••••••" value={loginPassword}
                onChange={e => { setLoginPassword(e.target.value); setLoginError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${loginError ? '#ef4444' : '#e8e8e6'}`, borderRadius: 10, fontSize: 14, fontFamily: "'Montserrat', sans-serif", outline: "none", boxSizing: "border-box", color: "#001c2b" }}
              />
              {loginError && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 6 }}>{loginError}</div>}
            </div>

            {/* Bouton */}
            <button onClick={handleLogin} style={{
              width: "100%", padding: "13px",
              background: loginEmail && loginPassword ? "linear-gradient(135deg,#00A5B2,#9CD14D)" : "#e8e8e6",
              color: loginEmail && loginPassword ? "#fff" : "#aaa",
              border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700,
              fontFamily: "'Montserrat', sans-serif",
              cursor: loginEmail && loginPassword ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                <polyline points="10 17 15 12 10 7"/>
                <line x1="15" y1="12" x2="3" y2="12"/>
              </svg>
              Se connecter
            </button>

            <div style={{ textAlign: "center", marginTop: 12 }}>
              <button onClick={() => { setShowLogin(false); window.dispatchEvent(new CustomEvent('pageChange', { detail: { page: 'hero' } })); }} style={{ background: "transparent", border: "none", fontSize: 12, color: "#00A5B2", cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }}>
                Pas encore de compte ? S'inscrire
              </button>
            </div>
          </div>
        </>
      )}
      {planetOpen && (
        <>
          <div onClick={() => setPlanetOpen(false)} style={{
            position: "fixed", inset: 0, zIndex: 9999990,
            background: "rgba(0,0,0,0.6)",
          }} />
          <div style={{
            position: "fixed",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999991,
            background: "#001c2b",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 20,
            padding: "1.8rem",
            width: "90vw",
            maxWidth: 480,
            maxHeight: "85vh",
            overflowY: "auto",
            boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
            fontFamily: "'Montserrat', sans-serif",
          }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:"1.2rem" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:40, height:40, borderRadius:10,
                  background:"rgba(156,209,77,0.12)",
                  border:"1px solid rgba(156,209,77,0.3)",
                  position:"relative", overflow:"hidden",
                }}>
                  <div style={{
                    position:"absolute", width:"130%", height:"130%", left:"-10%", top:"-2%",
                    backgroundImage:`url(${FOX_IMG_URL})`,
                    backgroundSize:"contain", backgroundRepeat:"no-repeat", backgroundPosition:"center 30%",
                  }} />
                </div>
                <div>
                  <div style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:"rgba(0,165,178,0.7)", textTransform:"uppercase" }}>
                    Renart te partage sa pépite verte
                  </div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:2 }}>
                    {new Date(pepite.date).toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric' })}
                  </div>
                </div>
              </div>
              <button onClick={() => setPlanetOpen(false)} style={{
                background:"transparent", border:"none", cursor:"pointer",
                color:"rgba(255,255,255,0.4)", fontSize:20, lineHeight:1, padding:4,
              }}>✕</button>
            </div>

            {/* Titre */}
            <h3 style={{
              fontSize:18, fontWeight:700, color:"#fff",
              lineHeight:1.3, marginBottom:"0.8rem",
            }}>
              {pepite.titre}
            </h3>

            {/* Texte */}
            <p style={{
              fontSize:14, color:"rgba(255,255,255,0.6)",
              lineHeight:1.7, marginBottom:"1.2rem",
            }}>
              {pepite.texte}
            </p>

            {/* Source */}
            <div style={{
              fontSize:11, color:"rgba(0,165,178,0.5)",
              fontStyle:"italic", marginBottom:"1.5rem",
            }}>
              Source : {pepite.source}
            </div>

            {/* Séparateur */}
            <div style={{ height:1, background:"rgba(255,255,255,0.07)", marginBottom:"1.2rem" }} />

            {/* Lien sensibilisation */}
            <a href={pepite.lien?.url || DEFAULT_PEPITE.lien.url} style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              background:"rgba(156,209,77,0.07)",
              border:"1px solid rgba(156,209,77,0.2)",
              borderRadius:12, padding:"1rem 1.1rem",
              textDecoration:"none",
              transition:"background 0.2s",
              gap: 12,
            }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(156,209,77,0.12)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(156,209,77,0.07)"}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#9CD14D", marginBottom:6, display:"flex", alignItems:"center", gap:6 }}>
                  🌍 {pepite.lien?.titre || DEFAULT_PEPITE.lien.titre}
                </div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight: 1.6 }}>
                  {pepite.lien?.texte || DEFAULT_PEPITE.lien.texte}
                </div>
              </div>
              <svg style={{ flexShrink: 0 }} width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 7h8M8 4l3 3-3 3" stroke="#9CD14D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </>
      )}
    </>
  );
}
